import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PushService } from '../../../services/push.service';
import { InstallService } from '../../../services/install.service';

/**
 * Shown when notification permission is still pending (or has been blocked)
 * AND the service worker is registered, so push is actually possible.
 *
 * Tapping "Enable" calls subscribeToPush(true) from inside a real user
 * gesture, which is required by iOS Safari 16.4+ for
 * Notification.requestPermission(). Without this banner, iOS users never
 * see the prompt at all (the auto-attempt in AppComponent.ngOnInit runs
 * outside a gesture and silently no-ops).
 *
 * Hidden on non-installed iOS Safari — push is impossible there until the
 * app is added to home screen, so the existing PWA install banner takes
 * precedence and we don't stack two banners on top of each other.
 */
@Component({
  selector: 'app-push-prompt-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (shouldShow) {
    <div class="push-prompt-banner">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <i class="fas fa-bell text-red-700 text-lg"></i>
        </div>
        <div class="flex-1 min-w-0">
          @if (push.permissionBlocked) {
            <div class="text-white font-bold text-sm">Notifications are blocked</div>
            <div class="text-red-200 text-xs mt-0.5">
              @if (install.isIos) {
                Open <strong>Settings → Notifications → RCMA CAD</strong> and turn on Allow Notifications.
              } @else {
                Tap the lock icon in your browser's address bar and allow notifications.
              }
            </div>
          } @else {
            <div class="text-white font-bold text-sm">Enable dispatch alerts</div>
            <div class="text-red-200 text-xs mt-0.5">Get push notifications for new calls</div>
          }
        </div>
        @if (!push.permissionBlocked) {
          <button (click)="enable()" [disabled]="working" class="bg-white text-red-700 font-black px-4 py-2 rounded-lg text-sm hover:bg-red-50 disabled:opacity-60 flex-shrink-0">
            @if (working) {
              <i class="fas fa-circle-notch fa-spin mr-1"></i>
            } @else {
              <i class="fas fa-bell mr-1"></i>
            }
            Enable
          </button>
        }
        <button (click)="dismiss()" aria-label="Dismiss" class="text-red-200 hover:text-white w-10 h-10 flex items-center justify-center rounded-lg hover:bg-red-800/50 flex-shrink-0"><i class="fas fa-times"></i></button>
      </div>
    </div>
    }
  `
})
export class PushPromptBannerComponent {
  working = false;

  constructor(
    public push: PushService,
    public install: InstallService
  ) {}

  get shouldShow(): boolean {
    // iOS in Safari (not standalone) — push is impossible until installed.
    // Defer to the install banner; don't double-stack prompts.
    if (this.install.isIos && !this.install.isStandalone) return false;
    return this.push.needsUserPrompt || this.push.permissionBlocked;
  }

  async enable(): Promise<void> {
    this.working = true;
    try {
      await this.push.subscribeToPush(true);
    } finally {
      this.working = false;
    }
  }

  dismiss(): void {
    this.push.dismissPrompt();
  }
}
